import useReveal from "../hooks/useReveal";
import { AlertTriangle, FlaskConical, Factory } from "lucide-react";

const RISKS = [
  {
    icon: AlertTriangle,
    title: "Computing Outback Risk",
    body: "Australia, along with its southern hemisphere neighbours, lags well behind leading international standards in access to supercomputing power that drives and enables research, technology development, medical advances and climate risk mitigation.",
    n: "01",
  },
  {
    icon: FlaskConical,
    title: "Scientific Leadership at Risk",
    body: "Without sovereign high-performance computing, Australia's world-class research capabilities face strategic limitations. This threatens our economic future and scientific leadership on the global stage.",
    n: "02",
  },
  {
    icon: Factory,
    title: "Industrial Policy Gap",
    body: "Australia lacks the sovereign computing infrastructure, tightly integrated with its research ecosystem, required for next-generation sovereign AI-Exascale capability development and innovation — creating a strategic vulnerability in our technological resilience.",
    n: "03",
  },
];

export default function Challenge() {
  const ref = useReveal();
  return (
    <section
      ref={ref}
      id="challenge"
      data-testid="challenge-section"
      className="relative py-28 md:py-40 border-t border-[var(--border-faint)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5 lg:sticky lg:top-32 self-start reveal">
          <div className="eyebrow mb-4">— 03 / The Challenge</div>
          <h2 className="font-display text-3xl md:text-5xl text-[var(--text)] leading-[1.05] tracking-tight mb-6">
            Australia faces a critical digital sovereignty risk.
          </h2>
          <p className="text-[var(--text-60)] leading-relaxed text-[15px] mb-10 max-w-md">
            Three converging risks threaten our technological independence
            and our economic future. Time is critical — Australia needs sovereign
            digital infrastructure to power research at unprecedented scale now.
          </p>
          <div className="inline-flex items-center gap-3 border border-[#dc2626]/60 dark:border-[#ef4444]/50 px-4 py-3">
            <span className="w-2 h-2 bg-[#dc2626] dark:bg-[#ef4444] animate-pulse" />
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#dc2626] dark:text-[#ef4444]">
              Time-Critical
            </span>
          </div>
        </div>

        <div className="lg:col-span-7 space-y-6">
          {RISKS.map((r, i) => {
            const Icon = r.icon;
            return (
              <div
                key={r.title}
                data-testid={`risk-card-${i}`}
                className="reveal group border border-[var(--border-subtle)] hover:border-[var(--color-yellow-deep)]/60 dark:hover:border-[var(--color-yellow)]/40 transition-colors p-7 md:p-10 relative"
                style={{
                  background: "var(--surface)",
                  transitionDelay: `${i * 100}ms`,
                }}
              >
                <div className="flex items-start gap-6">
                  <div className="font-mono text-[var(--color-yellow-deep)] dark:text-[var(--color-yellow)] text-sm tracking-widest pt-1">
                    {r.n}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <Icon
                        size={18}
                        className="text-[var(--color-yellow-deep)] dark:text-[var(--color-yellow)]"
                        strokeWidth={1.5}
                      />
                      <h3 className="font-display text-[var(--text)] text-xl md:text-2xl tracking-tight">
                        {r.title}
                      </h3>
                    </div>
                    <p className="text-[var(--text-65)] text-[15px] leading-relaxed">
                      {r.body}
                    </p>
                  </div>
                </div>
                <span className="absolute right-6 top-6 font-mono text-[10px] tracking-[0.2em] uppercase text-[#dc2626]/80 dark:text-[#ef4444]/80 group-hover:text-[#dc2626] dark:group-hover:text-[#ef4444] transition-colors">
                  Risk →
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
